/* Career roadmap view: level, gap, pay path, 3 phases with tickable steps, this week's move. */
import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Check, RotateCw, AlertTriangle, Zap, BookOpen, Award, Hammer, Users, Send } from "lucide-react";

const KIND = { skill: [BookOpen, "Skill"], cert: [Award, "Credential"], project: [Hammer, "Project"], network: [Users, "People"], apply: [Send, "Move"] };
const LEVEL_COLORS = { 1: "#b5694f", 2: "#c9a25c", 3: "#7a8f7a", 4: "#2f4f3e", 5: "#1a2520" };

export function CareerRoadmap({ res, onChanged, onRetake }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const p = res.plan;
  const toggle = async (id) => {
    setBusy(id); setErr("");
    try { const { data } = await api.post(`/career/steps/${id}/toggle`); onChanged(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="career-plan">
      <div className="rounded-2xl bg-ink text-cream p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full opacity-20" style={{ background: LEVEL_COLORS[res.level] }} />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-end gap-4">
            <span className="font-mono text-5xl leading-none" data-testid="career-level">{res.level}</span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Career level</div>
              <div className="font-serif text-2xl" data-testid="career-level-name">{res.level_name}</div>
              <div className="text-xs text-sage-light mt-1" data-testid="career-progress-text">{res.steps_done} of {res.steps_total} steps done · {res.progress}%</div>
            </div>
          </div>
          <div className="text-right text-xs text-sage-light">
            <div className="font-mono">{new Date(res.created_at).toLocaleDateString()}</div>
            {res.can_retake
              ? <button onClick={onRetake} className="mt-2 inline-flex items-center gap-1.5 bg-gold text-white rounded-md px-3 py-1.5 text-xs font-medium" data-testid="career-retake"><RotateCw className="w-3 h-3" /> Retake quiz</button>
              : <div className="mt-1" data-testid="career-retake-countdown">Retake in {res.days_until_retake} day{res.days_until_retake === 1 ? "" : "s"}</div>}
          </div>
        </div>
        <div className="relative mt-4 h-1.5 rounded-full bg-white/15"><div className="h-full rounded-full bg-gold transition-all" style={{ width: `${res.progress}%` }} /></div>
        <h3 className="relative font-serif text-3xl sm:text-4xl mt-6" data-testid="career-headline">{p.headline}</h3>
        <p className="relative text-sm text-sand mt-2 max-w-2xl" data-testid="career-gap">{p.gap_summary}</p>
        <div className="relative mt-5 flex items-center gap-6 flex-wrap text-xs">
          <div><div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Timeline</div><div className="text-cream mt-0.5" data-testid="career-timeline">{p.timeline}</div></div>
          <div className="flex items-center gap-3" data-testid="career-pay-path">
            {(p.pay_trajectory || []).map((t, i) => (
              <div key={i} className="flex items-center gap-3">
                <div><div className="text-[10px] uppercase tracking-wider text-sage">{t.when}</div><div className="font-mono text-cream">{t.pay}</div></div>
                {i < p.pay_trajectory.length - 1 && <span className="text-sage">→</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-forest text-white p-6" data-testid="career-this-week">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-mint inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> This week&apos;s move</div>
        <p className="font-serif text-xl mt-2">{p.this_week}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {p.phases.map((ph, pi) => (
          <div key={pi} className="bg-linen border border-sand rounded-2xl p-5" data-testid={`career-phase-${pi}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Phase {pi + 1} · {ph.timeframe}</div>
            <div className="font-serif text-xl mt-1">{ph.title}</div>
            <ul className="mt-3 space-y-2">
              {ph.steps.map((s) => {
                const [Icon, label] = KIND[s.kind] || KIND.skill;
                return (
                  <li key={s.id}>
                    <button onClick={() => toggle(s.id)} disabled={busy === s.id} className="w-full text-left flex items-start gap-2.5 group" data-testid={`career-step-${s.id}`} aria-pressed={!!s.done}>
                      <span className={`mt-0.5 w-4 h-4 rounded border shrink-0 inline-flex items-center justify-center transition-colors ${s.done ? "bg-forest border-forest text-white" : "border-sand bg-white group-hover:border-forest"}`}>{s.done && <Check className="w-3 h-3" />}</span>
                      <span className={`text-sm leading-snug ${s.done ? "line-through text-sage" : "text-ink"}`}>{s.text}<span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-sage align-middle"><Icon className="w-3 h-3" /> {label}</span></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {p.watch_out && <div className="flex items-start gap-3 bg-clay-light border border-clay/30 rounded-xl p-4 text-sm text-ink" data-testid="career-watch-out"><AlertTriangle className="w-4 h-4 text-clay mt-0.5 shrink-0" /><span><strong>Watch out:</strong> {p.watch_out}</span></div>}
      {err && <div className="text-sm text-clay" data-testid="career-step-error">{err}</div>}
    </div>
  );
}
